import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Controller, FieldError, FieldErrorsImpl, Merge, useFormContext } from 'react-hook-form';
import { ISatisfactionSurvey } from '../../../../../extViewmodels';
import { CustomDateRange } from '../../../../components/CustomDateRange';
import { DeprecatedTextArea } from '../../../../components/DeprecatedTextArea';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { RadioButton } from '../../../../components/RadioButton';
import { TextInputFormField } from '../../../../components/TextInputFormField';
import { Toggle } from '../../../../components/Toggle';
import { THEME } from '../../../../components-v2/Theme/theme';
import { baseStyleSheet } from '../../../../styles/styles';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { TagOnCompletion } from '../../shared/components/TagOnCompletion';
import { styleSheet } from '../styles';

enum BasicInfoInputIds {
	CompanyName = 'edit-survey-company-name-input',
	IndefDateRange = 'edit-survey-indef-date-range-input',
	Intro = 'edit-survey-intro-input',
	Name = 'edit-survey-name-input',
	SpecificDateRange = 'edit-survey-specific-date-range-input',
}

const ErrorMessage = ({ error }: { error: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>> }) => {
	if (!error) {
		return null;
	}
	return <p className={css(styleSheet.errors)}>{error as React.ReactNode}</p>;
};

export const BasicSurveyInfo: React.FC<{ survey: ISatisfactionSurvey }> = ({ survey }) => {
	const isArchived = !!survey?.archivedDate;
	const { control, watch, reset, formState } = useFormContext();
	const formValues = watch();

	// #region Date range modal
	const [dateRangeModalIsOpen, setDateRangeModalIsOpen] = React.useState<boolean>(false);
	const onEditDateRangeClicked = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
		setDateRangeModalIsOpen(true);
		e.stopPropagation();
		e.preventDefault();
	}, []);
	// #endregion

	const handleCloseDateRangeModal = React.useCallback(() => {
		setDateRangeModalIsOpen(false);
	}, []);

	const hasDateRange = !!formValues.startDate && !!formValues?.expirationDate;

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Basic Info</h3>
			<div className={css(styleSheet.constrainedFieldGroup)}>
				<div className={css(styleSheet.label)}>
					<span>
						Survey Intro <span className={css(baseStyleSheet.required)}>*</span>
					</span>
				</div>
				<Controller
					name='intro'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<DeprecatedTextArea
							disabled={isArchived}
							inputClassName={css(styleSheet.introTextArea)}
							inputId={BasicInfoInputIds.Intro}
							{...fieldProps}
						/>
					)}
				/>
				<ErrorMessage error={formState.errors?.intro?.message} />
			</div>
			<div className={css(styleSheet.constrainedFieldGroup)}>
				<Controller
					name='companyDisplayName'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<TextInputFormField
							disabled={isArchived}
							id={BasicInfoInputIds.CompanyName}
							label={
								<span>
									Company Name <span className={css(baseStyleSheet.required)}>*</span>
								</span>
							}
							labelStyles={[styleSheet.label]}
							type='text'
							{...fieldProps}
						/>
					)}
				/>
				<ErrorMessage error={formState.errors?.companyDisplayName?.message} />
			</div>

			<div className={css(styleSheet.fieldGroup)}>
				<div className={css(styleSheet.label)}>Survey Date Range</div>
				<Controller
					name='indefinitely'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<div className={css(styleSheet.radioButtons)}>
							<RadioButton
								checked={!hasDateRange}
								disabled={isArchived}
								id={BasicInfoInputIds.IndefDateRange}
								onChange={() => {
									fieldProps.onChange(true);
									reset(values => ({
										...values,
										expirationDate: undefined,
										startDate: new Date().toISOString(),
									}));
								}}
							>
								<span>Survey is available indefinitely into the future</span>
							</RadioButton>

							<Popover open={dateRangeModalIsOpen} onOpenChange={handleCloseDateRangeModal}>
								<PopoverTrigger>
									<RadioButton
										checked={hasDateRange}
										disabled={isArchived}
										id={BasicInfoInputIds.SpecificDateRange}
										onChange={() => {
											fieldProps.onChange(false);
											setDateRangeModalIsOpen(true);
										}}
									>
										<span>
											{`Survey is only available for a specific date range${hasDateRange ? ':' : ''}`}
											&nbsp;
										</span>
									</RadioButton>
								</PopoverTrigger>
								<PopoverContent side='right'>
									<CustomDateRange
										minDate={new Date()}
										from={formValues.startDate ? new Date(formValues.startDate) : undefined}
										to={formValues.expirationDate ? new Date(formValues.expirationDate) : undefined}
										resetDates={() => {
											reset(values => ({
												...values,
												expirationDate: undefined,
												startDate: new Date().toISOString(),
											}));
										}}
										endDateTextLabel='Expiration Date'
										endDateRequired={false}
										onChange={(from, to) => {
											reset(values => {
												const fromDateStr = moment(from).toISOString();
												return {
													...values,
													startDate: fromDateStr,
													expirationDate: to ? moment(to).toISOString() : fromDateStr,
												};
											});
										}}
									/>
								</PopoverContent>
							</Popover>
							{hasDateRange && (
								<button
									className={css(baseStyleSheet.brandLink)}
									disabled={isArchived}
									onClick={onEditDateRangeClicked}
								>
									<span>
										{`${moment(formValues.startDate).format('MM/DD/yyyy')} - ${moment(formValues.expirationDate).format(
											'MM/DD/yyyy'
										)}`}
									</span>
								</button>
							)}
						</div>
					)}
				/>
				<div className={css(styleSheet.tagOnCompletionContainer)}>
					<TagOnCompletion />
				</div>
				<FormFieldWrapper
					name='shouldHideFeedback'
					control={control}
					title='Hide feedback box'
					description='Hide the open comments box from survey responses'
				>
					{({ value, onChange }) => (
						<Toggle
							disabled={isArchived}
							id='edit-survey-hide-feedback-toggle'
							isOn={!!value}
							onToggleCheckChanged={onChange}
							uncheckedColor={THEME.colors.icon.gray}
						/>
					)}
				</FormFieldWrapper>
			</div>
		</section>
	);
};
